import type { RouterOutputs } from "@repo/api";
import type { ColumnDef } from "@tanstack/react-table";
import React, { useState } from "react";
import { Permissions } from "@repo/database";
import { formatIntToCurrency } from "@repo/utils";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useOrder } from "~/hooks/use-order";
import { api } from "~/lib/api";
import { usePermissionManager } from "~/managers/use-permission-manager";
import { useOrderStore } from "~/stores/use-order-store";
import { DataTable } from "../miscellaneous/data-table";
import { DataTableColumnHeader } from "../miscellaneous/data-table-column-header";
import { DateNav } from "../miscellaneous/date-nav";
import { ScrollArea } from "../ui/scroll-area";

type DataReceipt =
  RouterOutputs["report"]["getDayOverview"]["receipts"][number];

type DataMutation =
  RouterOutputs["report"]["getDayOverview"]["mutations"][number];

type CombinedData = DataReceipt | DataMutation;

function isReceipt(data: DataReceipt | DataMutation): data is DataReceipt {
  return (data as DataReceipt).number !== undefined;
}

export default function DayOverview() {
  const { t } = useTranslation("common");

  const utils = api.useUtils();

  const { orders } = useOrder();

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const { setReceiptDialogOpen, setSelectedReceipt } = useOrderStore();

  const { data } = api.report.getDayOverview.useQuery(selectedDate);

  const { checkPermission, currentEmployee } = usePermissionManager();

  const total = data?.receipts.reduce((acc, curr) => acc + curr.total, 0) ?? 0;

  const unRealizedTotal = orders.reduce((acc, curr) => {
    return (
      acc +
      curr.items.reduce((acc, curr) => acc + curr.value * curr.quantity, 0)
    );
  }, 0);

  const [combinedData, setCombinedData] = useState<CombinedData[]>([]);

  React.useEffect(() => {
    if (data) {
      const combined: CombinedData[] = [...data.receipts, ...data.mutations];
      setCombinedData(combined);
    }
  }, [data]);

  const handleSelectRow = async (row: DataReceipt | DataMutation) => {
    // fetch the receipt
    if (!isReceipt(row)) return null;

    const receipt = await utils.report.getReceiptByNumber.fetch(row.number);
    setSelectedReceipt({
      receipt,
      showCredit: true,
    });
    setReceiptDialogOpen(true);
  };

  const columns: ColumnDef<DataReceipt | DataMutation>[] = [
    {
      id: t("Report.date"),
      accessorKey: "createdAt",
      filterFn: "includesString",
      sortingFn: (rowA, rowB) => {
        return (
          rowA.original.createdAt.getTime() - rowB.original.createdAt.getTime()
        );
      },
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.date")} />
      ),
      cell: ({ row: { original: receipt } }) => {
        return <p>{format(receipt.createdAt, "Pp")}</p>;
      },
    },
    {
      id: t("Report.reference"),
      accessorKey: "number",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.reference")} />
      ),
      cell: ({ row: { original: receipt } }) => {
        if (!isReceipt(receipt)) return <p>{t("Report.open_drawer")}</p>;
        return <p>{"#" + receipt.number}</p>;
      },
    },
    {
      id: t("Report.total_price"),
      accessorKey: "total",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.total_price")}
        />
      ),
      cell: ({ row: { original: receipt } }) => {
        if (!isReceipt(receipt)) return null;

        return <p>{formatIntToCurrency(receipt.total)}</p>;
      },
    },
    {
      id: t("Report_payment_method"),
      accessorKey: "paymentMethods",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report_payment_method")}
        />
      ),
      cell: ({ row: { original: receipt } }) => {
        if (!isReceipt(receipt)) return null;

        return <p>{receipt.paymentMethods.join(", ")}</p>;
      },
    },
    {
      id: t("Report_table"),
      accessorKey: "table",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report_table")} />
      ),
      cell: ({ row: { original: receipt } }) => {
        if (!isReceipt(receipt)) return null;
        return <p>{receipt.tableReference}</p>;
      },
    },
    {
      id: t("Report.employee_name"),
      accessorKey: "table",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.employee_name")}
        />
      ),
      cell: ({ row: { original: receipt } }) => {
        return <p>{receipt.employee}</p>;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-2 relative">
      <DateNav
        className="justify-center border-b py-2"
        date={selectedDate}
        onNavigate={setSelectedDate}
      />
      {checkPermission(Permissions.view_reports, currentEmployee()) && (
        <span className="text-center font-bold">
          {`${t("Report.unrealized_revenue")}: ${formatIntToCurrency(unRealizedTotal)}`}
        </span>
      )}
      {checkPermission(Permissions.view_reports, currentEmployee()) && (
        <span className="text-center font-bold">
          {`${t("Report.revenue")}: ${formatIntToCurrency(total)}`}
        </span>
      )}

      <ScrollArea className="max-h-[70vh] p-4">
        <DataTable
          className="h-[40vh]"
          defaultSorting={[{ id: t("Report.date"), desc: true }]}
          onSelect={(row) => {
            void handleSelectRow(row);
          }}
          columns={columns}
          data={combinedData}
        />
      </ScrollArea>
    </div>
  );
}
